/**
 * Guarantees that at most one address is flagged as billing before the data
 * leaves the form. Keeps the first address already flagged as billing, or
 * promotes the first address when none is flagged. Returns a new array of new
 * objects (never mutates the input).
 *
 * @param {Array<{isBilling?: boolean}>} addresses
 * @returns {Array<{isBilling: boolean}>}
 */
export const normalizeBillingAddresses = (addresses) => {
  if (!Array.isArray(addresses) || addresses.length === 0) return [];

  const firstBillingIndex = addresses.findIndex((addr) => addr.isBilling === true);
  const billingIndex = firstBillingIndex === -1 ? 0 : firstBillingIndex;

  return addresses.map((addr, index) => ({
    ...addr,
    isBilling: index === billingIndex,
  }));
}

export const mapCreateRequestData = (data) => {
  return {
    "firstName": data.firstName,
    "lastName": data.type !== 'company' ? data.lastName : '',
    "email": data.email,
    "phoneNumber": data.phoneNumber,
    "company": data.company,
    "companyId": data.companyId,
    "titleId": data.titleId,
    "function": data.function,
    "type": data.type,
    "customer": data.customer ?? false,
    "vendor": data.vendor ?? false,
    "landlord": data.landlord ?? false,
    "vat": data.vat,
    "internalNote": data.internalNote,
    "addresses": normalizeBillingAddresses(data.addresses),
    "lang": data.lang,
    "tags": data.tags,
    "salesPersonId": data.salesPersonId
  }
}

export const mapEditRequestData = (data) => {
  return {
    ...mapCreateRequestData(data),
    id: data.id,
  };
}

const mapAddress = (addr) => ({
  streetAddress1: addr.streetAddress1 ?? '',
  streetAddress2: addr.streetAddress2 ?? '',
  city: addr.city ?? '',
  stateProvinceId: addr.stateProvinceId ?? null,
  postalCode: addr.postalCode ?? '',
  countryId: addr.countryId ?? null,
  isBilling: addr.isBilling ?? '',
  addressType: addr.addressType ?? ''
});

export function prepareContactFormData(contact) {
  const belongsToCompany = !contact.isCompany && !!contact.companyId;
  let addresses = contact.addresses || [];
  if (belongsToCompany) {
    addresses = [];
  }
  const parentCompany = contact.parentCompany ?? null;
  const parentCompanyName = belongsToCompany
    ? (parentCompany?.firstName ?? contact.company ?? null)
    : null;
  const parentCompanyAddresses = belongsToCompany
    ? (parentCompany?.addresses ?? []).map(mapAddress)
    : [];

  return {
    id: contact.id,
    firstName: contact.firstName ?? '',
    lastName: !contact.isCompany ? (contact.lastName ?? '') : '',
    email: contact.email ?? '',
    phoneNumber: contact.phoneNumber ?? '',
    companyId: contact.companyId ?? null,
    titleId: contact.titleId ?? null,
    function: contact.function ?? '',
    type: contact.isCompany ? 'company' : 'individual',
    customer: contact.isCustomer,
    vendor: contact.isVendor,
    landlord: contact.isLandlord,
    vat: contact.vat ?? '',
    internalNote: contact.internalNote ?? '',
    lang: contact.lang ?? 'en_US',
    tags: contact.tags ?? [],
    salesPersonId: contact.salesPersonId ?? null,
    addresses: addresses.map(mapAddress),
    parentCompanyName,
    parentCompanyAddresses,
    childContacts: (contact.childContacts ?? []).map(child => ({
      id: child.id,
      firstName: child.firstName ?? '',
      lastName: child.lastName ?? '',
      email: child.email ?? '',
      function: child.function ?? ''
    }))
  };
}

